import { toast } from 'react-toastify';

import { getAllTests } from './action-test';
import { clearTimer, pauseTimer, resumeTimer, startTimer } from './action-timer';
import {
  clearInstructionTimer,
  startInstructionTimer,
} from './action-instruction-timer';
import { openInNewTab } from '../../constants/url';
import { checkOnline } from '../../hooks/useNetworkStatus';
import * as Cookies from '../../lib/Cookie';
import {
  deleteTestLaunchData,
  dexieEndTest,
  dexieSyncTest,
  getAllTracks,
  getTestLaunchData,
  getTestToken,
  putTracks,
} from '../../lib/dexie';
import Service from '../../lib/Service';
import { removeHtmlTags } from '../../lib/utils';
import { engineActions as types } from '../actionTypes';

const groupBy = (xs, key) =>
  xs.reduce((rv, x) => {
    if (x[key]) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
    }
    return rv;
  }, {});

const groupByPassage = (xs, key) =>
  xs.reduce((rv, x, index) => {
    const groupKey = x[key] != null ? x[key] : `__NO_PASSAGE__${x.ItemID ?? index}`;
    (rv[groupKey] = rv[groupKey] || []).push(x);
    return rv;
  }, {});

const extractResponses = tracks => {
  const groupedTracks = groupBy(tracks, 'itemId');

  const responses = Object.keys(groupedTracks).map(item => {
    const allTracks = groupedTracks[item];
    const lastTrack = allTracks[allTracks.length - 1];
    return {
      sectionid: Number(lastTrack.sectionId),
      itemId: Number(lastTrack.itemId),
      selectedOptionId: lastTrack.selectedOptionId,
      enteredText: lastTrack.enteredText,
      isCorrect: lastTrack.isCorrect,
      marked: lastTrack.marked,
      timeTaken: lastTrack.timeTaken,
      attempt: lastTrack.attempt,
    };
  });

  return responses;
};

// Submit evaluation test action
export const submitEvaluationTest = ({ testId, courseId, responses }) => async (dispatch, getState) => {
  try {
    dispatch({ type: types.TEST_SUBMITTING, payload: { submitting: true } });
    const { user } = getState().auth;
    const payload = {
      testId,
      courseId,
      studentId: user.id,
      responses,
    };
    const res = await Service.POST({
      name: 'submit-evaluation-test',
      payload,
      testengine: true,
    });
    if (res?.success) {
      toast.success('Test submitted successfully!');
      dispatch({ type: types.TEST_SUBMITTED, payload: { submitting: false, submitted: true } });
    } else {
      toast.error('Test submission failed.');
      dispatch({ type: types.TEST_SUBMITTED, payload: { submitting: false, submitted: false } });
    }
    return res;
  } catch (error) {
    toast.error('Test submission error.');
    dispatch({ type: types.TEST_SUBMITTED, payload: { submitting: false, submitted: false } });
    throw error;
  }
};

const savetrack = (data, question, timeRemaining = 0) => {
  const d = new Date();
  const datetime = d.toISOString();


  const newid = `${data.companyCode}_${data.studentId}_${data.testId}_${question.QuestionPaperSectionID}_${question.ItemID}_${data.trackNumber}`;

  return {
    _id: newid,
    testId: data.testId,
    studentId: data.studentId,
    companyCode: data.companyCode,
    itemId: question.ItemID,
    sectionId: question.QuestionPaperSectionID,
    sectionIndex: question.qsectionindex,
    sectionName: question.qsectionname?.replace('\t', '') || '',
    questionIndex: question.qindexbysection,
    subjectId: question.SubjectID,
    subjectName: question.SubjectName,
    areaId: question.AreaID,
    areaName: question.AreaName,
    topicId: question.TopicID,
    topicName: question.TopicName,
    difficultyId: question.DifficultyLevel,
    difficultyName: question.Difficulty,
    timeRemaining,
    isSectionCompleted: null,
    selectedOptionId: data.selectedOptionId,
    attempt: data.attempt,
    itemType: question.ItemType,
    marked: data.marked,
    timeTaken: data.timeTaken || 0,
    timeStamp: datetime,
    points: question.Points,
    negativePoints: question.NegativePoints,
    isCorrect: data.isCorrect,
    enteredText: data.enteredText,
    trackNumber: data.trackNumber,
    saved: 0,
    offlineSaved: 0,
  };
};

const frameStudentTracks = (question, selectedValues = {}, state) => {
  const { testId, studentId, companyCode, trackNumber, currentTime, tracks } =
    state;

  /* save response */
  const timeRemaining = currentTime;

  const trackItem = savetrack(
    {
      testId,
      studentId,
      companyCode,
      selectedOptionId: selectedValues?.selectedOptionId,
      attempt: selectedValues?.attempt,
      isCorrect: selectedValues?.isCorrect,
      enteredText: selectedValues?.enteredText,
      timeTaken: selectedValues?.timeTaken,
      marked: selectedValues?.marked,
      trackNumber,
    },
    question,
    timeRemaining,
  );

  const updatedTracks = tracks.slice();
  updatedTracks.push(trackItem);

  const trackData = {
    tracks: updatedTracks,
    trackNumber: trackNumber + 1,
  };

  return trackData;
};

const getQuestionResponses = (studentResponse, question) => {
  let responseList = studentResponse.slice();
  const questionRes = responseList.filter(
    p =>
      p.sectionid === question.QuestionPaperSectionID &&
      p.itemId === question.ItemID,
  );

  let updateObj = {
    sectionid: question.QuestionPaperSectionID,
    itemId: question.ItemID,
    selectedOptionId: null,
    enteredText: null,
    isCorrect: null,
    marked: null,
    attempt: 1,
    eliminators: [],
    unmasks: [],
  };

  if (questionRes.length > 0) {
    updateObj = {
      sectionid: question.QuestionPaperSectionID,
      itemId: question.ItemID,
      selectedOptionId: questionRes[0].selectedOptionId,
      enteredText: questionRes[0].enteredText,
      isCorrect: questionRes[0].isCorrect,
      marked: questionRes[0].marked,
      attempt: questionRes[0].attempt,
      eliminators: questionRes[0].eliminators || [],
      unmasks: questionRes[0].unmasks || [],
    };
  }
  return updateObj;
};

const normalizeInstruction = instr => ({
  id: instr.id,
  title: instr.title || 'Instructions',
  content: instr.content || '',
  timer: Number(instr.timer) || 0,
  sectionId: String(instr.sectionId ?? '0'),
  orderNo: Number(instr.orderNo) || 0,
  audioUrl: instr.audioUrl || '',
  bgAudioUrl: instr.bgAudioUrl || '',
  instructionType: instr.instructionType || '',
});

const buildInstructionMaps = sectionResponses => {
  const allInstructions = [];

  sectionResponses.forEach(section => {
    (section.Instructions || []).forEach(instr => {
      allInstructions.push(normalizeInstruction(instr));
    });
  });

  const globalInstructions = allInstructions
    .filter(i => i.sectionId === '0')
    .sort((a, b) => a.orderNo - b.orderNo);

  const sectionInstructionsMap = {};
  allInstructions
    .filter(i => i.sectionId !== '0')
    .forEach(instr => {
      if (!sectionInstructionsMap[instr.sectionId]) {
        sectionInstructionsMap[instr.sectionId] = [];
      }
      sectionInstructionsMap[instr.sectionId].push(instr);
    });

  Object.keys(sectionInstructionsMap).forEach(key => {
    sectionInstructionsMap[key].sort((a, b) => a.orderNo - b.orderNo);
  });

  return {
    instructions: allInstructions,
    globalInstructions,
    sectionInstructionsMap,
  };
};

const getGroupSectionId = grp => {
  if (!grp?.questions?.length) return null;
  return String(grp.questions[0].QuestionPaperSectionID);
};

const navigateToPosition = newPos => (dispatch, getState) => {
  const { questionGrps, totalQuestionGrps, sections } = getState().engine;
  const activeQuestion = questionGrps[newPos];
  const sectionId = getGroupSectionId(activeQuestion);
  const activeSection =
    sections.find(s => String(s.sectionid) === sectionId) || sections[0];

  dispatch({
    type: types.SET_TEST_DATA,
    payload: {
      activeQuestion,
      currentPsgPos: newPos,
      isFirstPsg: newPos === 0,
      isLastPsg: newPos === totalQuestionGrps - 1,
      activeSection,
    },
  });
};

const startMainTimerIfNeeded = () => (dispatch, getState) => {
  const { mainTimerStarted, totalDuration } = getState().engine;
  if (!mainTimerStarted && totalDuration > 0) {
    dispatch(startTimer(totalDuration));
    dispatch({
      type: types.SET_TEST_DATA,
      payload: { mainTimerStarted: true },
    });
  } else if (mainTimerStarted) {
    dispatch(resumeTimer());
  }
};

export const completeInstructionPhase = pendingNav => (dispatch, getState) => {
  const { sections, sectionInstructionsMap, shownInstructionSections } =
    getState().engine;

  dispatch(clearInstructionTimer());
  dispatch({
    type: types.END_INSTRUCTION,
    payload: {
      showInstruction: false,
      activeInstruction: null,
      instructionQueue: [],
      instructionTimeLeft: 0,
      pendingSectionNav: null,
    },
  });

  if (!pendingNav) {
    dispatch(startMainTimerIfNeeded());
    return;
  }

  if (pendingNav.type === 'startTest') {
    const firstSectionId = String(sections[0]?.sectionid);
    const sectionQueue = sectionInstructionsMap[firstSectionId] || [];
    if (
      sectionQueue.length > 0 &&
      !shownInstructionSections.includes(firstSectionId)
    ) {
      dispatch(beginInstructionPhase(sectionQueue, {
        type: 'startQuestions',
        newPos: 0,
        sectionId: firstSectionId,
      }));
      return;
    }
    dispatch(navigateToPosition(0));
    dispatch(startMainTimerIfNeeded());
    return;
  }

  if (pendingNav.type === 'startQuestions') {
    if (pendingNav.sectionId) {
      dispatch({
        type: types.SET_TEST_DATA,
        payload: {
          shownInstructionSections: [
            ...shownInstructionSections,
            String(pendingNav.sectionId),
          ],
        },
      });
    }
    dispatch(navigateToPosition(pendingNav.newPos ?? 0));
    dispatch(startMainTimerIfNeeded());
    return;
  }

  if (pendingNav.type === 'navigate') {
    if (pendingNav.sectionId) {
      dispatch({
        type: types.SET_TEST_DATA,
        payload: {
          shownInstructionSections: [
            ...shownInstructionSections,
            String(pendingNav.sectionId),
          ],
        },
      });
    }
    dispatch(navigateToPosition(pendingNav.newPos));
    dispatch(resumeTimer());
  }
};

export const advanceInstruction = () => (dispatch, getState) => {
  dispatch(clearInstructionTimer());

  const { instructionQueue, pendingSectionNav } = getState().engine;

  if (instructionQueue.length > 0) {
    const [activeInstruction, ...remainingQueue] = instructionQueue;
    dispatch({
      type: types.SET_TEST_DATA,
      payload: {
        activeInstruction,
        instructionQueue: remainingQueue,
        instructionTimeLeft:
          activeInstruction.timer > 0 ? activeInstruction.timer : 0,
      },
    });
    if (activeInstruction.timer > 0) {
      dispatch(
        startInstructionTimer(activeInstruction.timer, () => {
          dispatch(advanceInstruction());
        }),
      );
    }
    return;
  }

  dispatch(completeInstructionPhase(pendingSectionNav));
};

export const beginInstructionPhase = (queue, pendingNav) => dispatch => {
  if (!queue || queue.length === 0) {
    dispatch(completeInstructionPhase(pendingNav));
    return;
  }

  dispatch(pauseTimer());

  const [activeInstruction, ...remainingQueue] = queue;

  dispatch({
    type: types.START_INSTRUCTION_PHASE,
    payload: {
      showInstruction: true,
      activeInstruction,
      instructionQueue: remainingQueue,
      pendingSectionNav: pendingNav || null,
      instructionTimeLeft:
        activeInstruction.timer > 0 ? activeInstruction.timer : 0,
    },
  });

  if (activeInstruction.timer > 0) {
    dispatch(
      startInstructionTimer(activeInstruction.timer, () => {
        dispatch(advanceInstruction());
      }),
    );
  }
};

export const checkSectionInstructions =
  (sectionId, pendingNav) => (dispatch, getState) => {
    const { sectionInstructionsMap, shownInstructionSections } =
      getState().engine;
    const sectionKey = String(sectionId);
    const queue = sectionInstructionsMap[sectionKey] || [];

    if (queue.length === 0 || shownInstructionSections.includes(sectionKey)) {
      dispatch(completeInstructionPhase(pendingNav));
      return;
    }

    dispatch(
      beginInstructionPhase(queue, {
        ...pendingNav,
        sectionId: sectionKey,
      }),
    );
  };

export const initInstructionFlow = () => (dispatch, getState) => {
  const { globalInstructions, sections } = getState().engine;

  if (globalInstructions.length > 0) {
    dispatch(
      beginInstructionPhase([...globalInstructions], { type: 'startTest' }),
    );
    return;
  }

  const firstSectionId = String(sections[0]?.sectionid);
  dispatch(
    checkSectionInstructions(firstSectionId, {
      type: 'startQuestions',
      newPos: 0,
      sectionId: firstSectionId,
    }),
  );
};

export const handleNavigateQn = isNext => async (dispatch, getState) => {
  const { currentPsgPos, questionGrps, totalQuestionGrps } = getState().engine;

  let newPos = currentPsgPos;
  if (isNext) {
    newPos = Math.min(currentPsgPos + 1, totalQuestionGrps - 1);
  } else {
    newPos = Math.max(currentPsgPos - 1, 0);
  }

  if (newPos === currentPsgPos) {
    return;
  }

  const currentSectionId = getGroupSectionId(questionGrps[currentPsgPos]);
  const targetSectionId = getGroupSectionId(questionGrps[newPos]);

  if (
    isNext &&
    targetSectionId &&
    currentSectionId &&
    targetSectionId !== currentSectionId
  ) {
    dispatch(
      checkSectionInstructions(targetSectionId, {
        type: 'navigate',
        newPos,
        sectionId: targetSectionId,
      }),
    );
    return;
  }

  dispatch(navigateToPosition(newPos));
};

export const gotoQuestion = qId => (dispatch, getState) => {
  const { questionGrps, totalQuestionGrps, currentPsgPos } = getState().engine;

  const psgNewPos = questionGrps.findIndex(
    p => p && Array.isArray(p.questions) && p.questions.some(q => q.ItemID === qId),
  );

  if (psgNewPos === -1) {
    return;
  }

  const currentSectionId = getGroupSectionId(questionGrps[currentPsgPos]);
  const targetSectionId = getGroupSectionId(questionGrps[psgNewPos]);

  if (
    psgNewPos > currentPsgPos &&
    targetSectionId &&
    currentSectionId &&
    targetSectionId !== currentSectionId
  ) {
    dispatch(
      checkSectionInstructions(targetSectionId, {
        type: 'navigate',
        newPos: psgNewPos,
        sectionId: targetSectionId,
      }),
    );
    return;
  }

  dispatch(navigateToPosition(psgNewPos));
};

export const handleInputValue =
  (e, question, inputIndex = null) =>
    (dispatch, getState) => {
      if (!e.target) {
        return;
      }
      const { value } = e.target;
      const { studentResponse } = getState().engine;
      const { ItemType } = question;

      let selected = getQuestionResponses(studentResponse, question);

      const responseList = studentResponse.slice();

      const responsePos = studentResponse.findIndex(
        s =>
          s.itemId === question.ItemID &&
          s.sectionid === question.QuestionPaperSectionID,
      );

      if (ItemType === 'essay') {
        selected.enteredText = value;
        selected.isCorrect = value ? false : null;
      }

      if (ItemType === 'an') {
        if (inputIndex !== null) {
          let selectedText =
            selected.enteredText?.split('~').filter(i => i) || [];
          const pos = selectedText.findIndex(s => s.startsWith(`${inputIndex}@`));

          if (pos === -1) {
            selectedText.push(`${inputIndex}@${value}`);
          } else {
            if (value) {
              selectedText.splice(pos, 1, `${inputIndex}@${value}`);
            } else {
              selectedText.splice(pos, 1);
            }
          }
          selected.enteredText = selectedText.join('~');
        } else {
          selected.enteredText = value;
        }
        selected.isCorrect = value ? false : null;

        const correctAnswers = question.ItemOptionResponse.filter(c => c.IsCorrect);

        if (correctAnswers.length > 0) {
          let correctAnswer = correctAnswers.at(0);
          let option = removeHtmlTags(correctAnswer.Options)?.trim();
          let optionSeg = option?.split('~');
          if (optionSeg && optionSeg.length) {
            selected.isCorrect = optionSeg.some(s => s.toLowerCase().trim() === value.toLowerCase().trim());
          }
        }
      }
      if (responsePos !== -1) {
        responseList.splice(responsePos, 1, selected);
      } else {
        responseList.push(selected);
      }

      dispatch({
        type: types.SAVE_ANSWER,
        payload: {
          studentResponse: responseList,
        },
      });
    };

export const handleSelectValue = (e, question) => (dispatch, getState) => {
  if (!e.target) {
    return;
  }
  const { value, checked } = e.target;
  const { studentResponse } = getState().engine;
  const { ItemType } = question;

  let selected = getQuestionResponses(studentResponse, question);

  const responseList = studentResponse.slice();

  const responsePos = studentResponse.findIndex(
    s =>
      s.itemId === question.ItemID &&
      s.sectionid === question.QuestionPaperSectionID,
  );

  if (ItemType === 'mcq') {
    const selectedAns = question.ItemOptionResponse.find(
      r => r.ItemOptionID === value,
    );

    selected.selectedOptionId = value;
    selected.isCorrect = selectedAns.IsCorrect;
  }

  if (ItemType === 'mca') {
    let prevSelected = selected.enteredText || '';
    let values = [];
    if (prevSelected) {
      values = [...prevSelected.split('~'), value];
    } else {
      values = [value];
    }
    if (!checked) {
      values = values.filter(v => v !== value);
    }

    const isCorrect = values.every(
      v =>
        question.ItemOptionResponse.find(r => r.ItemOptionID === v)?.IsCorrect,
    );
    selected.enteredText = values.join('~');
    selected.isCorrect = isCorrect;
  }

  if (responsePos !== -1) {
    responseList.splice(responsePos, 1, selected);
  } else {
    responseList.push(selected);
  }

  dispatch({
    type: types.SAVE_ANSWER,
    payload: {
      studentResponse: responseList,
    },
  });

  dispatch(frameTrack(e, question));
};

// Used by uploadEssay type to store the uploaded file URL as the answer.
// For the UI/flow in this app, having `isCorrect !== null` marks a question as answered.
export const setUploadEssayAnswer = (question, enteredText) => (dispatch, getState) => {
  const { studentResponse } = getState().engine;

  const selected = getQuestionResponses(studentResponse, question);
  const responseList = studentResponse.slice();

  const responsePos = studentResponse.findIndex(
    s =>
      s.itemId === question.ItemID &&
      s.sectionid === question.QuestionPaperSectionID,
  );

  selected.selectedOptionId = null;
  selected.enteredText = enteredText;
  selected.isCorrect = enteredText ? false : null;

  if (responsePos !== -1) {
    responseList.splice(responsePos, 1, selected);
  } else {
    responseList.push(selected);
  }

  dispatch({
    type: types.SAVE_ANSWER,
    payload: {
      studentResponse: responseList,
    },
  });
};

const validateQuestionGroupResponse =
  (value, question) => (dispatch, getState) => {
    const { studentResponse, activeQuestion } = getState().engine;
    const { ItemType } = question;

    if (ItemType === 'an') {
      if (question.QuestionGroupId) {
        const qnGrps = activeQuestion.questions.filter(
          i => i.QuestionGroupId === question.QuestionGroupId,
        );
        const responses = qnGrps.filter(s => {
          const res = studentResponse.find(
            q =>
              q.itemId === s.ItemID && q.sectionid === s.QuestionPaperSectionID,
          );
          if (res && res.enteredText?.toLowerCase() === value?.toLowerCase()) {
            return true;
          }
          return false;
        });
        const isExist = responses.length > 1;
        if (isExist) {
          dispatch(handleInputValue({ target: { value: '' } }, question));
          alert('Please enter different option');
        }
      }
    }
  };

export const frameTrack = (e, question) => async (dispatch, getState) => {
  const { ItemType } = question;

  if (ItemType === 'an' && e) {
    const { value } = e.target;
    await dispatch(validateQuestionGroupResponse(value, question));
  }
  const { currentTime } = getState().timer;

  const {
    studentResponse,
    testId,
    studentId,
    companyCode,
    trackNumber,
    tracks,
    prevTime,
  } = getState().engine;

  let selected = getQuestionResponses(studentResponse, question);
  const timeTaken = prevTime - currentTime;

  const framedData = frameStudentTracks(
    question,
    {
      ...selected,
      timeTaken,
    },
    {
      testId,
      studentId,
      companyCode,
      trackNumber,
      tracks,
      currentTime,
    },
  );

  dispatch({
    type: types.SAVE_ANSWER,
    payload: {
      ...framedData,
      prevTime: currentTime,
    },
  });
};

export const setBookmarkQn = value => async dispatch => {
  dispatch({
    type: types.SAVE_ANSWER,
    payload: { marked: value },
  });
};

const getInitTest = async (testId, courseId) => {
  const initTest = await Service.POST({
    name: 'test',
    payload: JSON.stringify({
      groupId: 0,
      testId: testId,
      courseId: courseId,
    }),
  }).then(res => {
    if (res.success) {
      return res.data;
    }
    throw new Error(res.message);
  });
  return initTest;
};

const getEngineToken = async token => {
  const resData = await Service.GET({
    name: 'validate-token',
    queryString: `token=${token}&companyCode=studentportal`,
    testengine: true,
  }).then(res => {
    if (res.success) {
      return res.data;
    }
    throw new Error(res.message);
  });
  return resData;
};

export const validateEngineToken = token => async dispatch => {
  try {
    dispatch({
      type: types.LAUNCH_TEST,
      payload: { launching: true },
    });

    const resData = await getEngineToken(token);
    const engineData = { ...(resData || {}) };
    delete engineData.token;

    const urlParams = new URLSearchParams(resData.redirectLink?.split('?')[1]);

    let redirectToken = urlParams.get('token');

    openInNewTab(`/test-engine?token=${redirectToken}`);

    dispatch({
      type: types.LAUNCH_TEST,
      payload: { launching: false, ...engineData },
    });
  } catch (error) {
    dispatch({
      type: types.LAUNCH_TEST,
      payload: { launching: false },
    });
  }
};

const suffleInnerItems = (questions, randNumber) => {
  const tempEvenQuestions = [];
  const tempOddQuestions = [];
  for (let i = 0; i < questions.length; i += 1) {
    if ((i + 1) % randNumber === 0) {
      tempEvenQuestions.push(questions[i]);
    } else {
      tempOddQuestions.push(questions[i]);
    }
  }
  return tempEvenQuestions.concat(tempOddQuestions);
};

const groupByName = (xs, key) =>
  xs.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});

const suffleItemsByPassage = (questions, randNumber) => {
  const tempEvenQuestions = [];
  const tempOddQuestions = [];
  const groupedQuestions = groupByName(questions, 'ItemPassageID');
  const responses = Object.keys(groupedQuestions);
  for (let i = 0; i < responses.length; i += 1) {
    const shuffledQuestions = suffleInnerItems(
      groupedQuestions[responses[i]],
      randNumber,
    );
    if ((i + 1) % randNumber === 0) {
      tempEvenQuestions.push(...shuffledQuestions);
    } else {
      tempOddQuestions.push(...shuffledQuestions);
    }
  }
  return tempEvenQuestions.concat(tempOddQuestions);
};

const randomizeQuestions = (questions, randNo, shuffleQuestionsBy) => {
  const randNumber = Number(randNo);
  if (randNumber > 0 && randNumber <= 6) {
    const questionsAfterShuffle = [];
    const groupedQuestions = groupByName(
      questions,
      shuffleQuestionsBy || 'QuestionPaperSectionID',
    );
    const responses = Object.keys(groupedQuestions);
    for (let i = 0; i < responses.length; i += 1) {
      const shuffledQuestions = suffleItemsByPassage(
        groupedQuestions[responses[i]],
        randNumber,
      );
      questionsAfterShuffle.push(...shuffledQuestions);
    }
    return questionsAfterShuffle;
  }
  return questions;
};

const getSectionsAndQuestions = (
  sectionResponses,
  random,
  shuffleQuestionsBy,
) => {
  const sectionResponse = sectionResponses;
  const sections = [];
  const questions = [];
  let qindex = 0;

  const instructionData = buildInstructionMaps(sectionResponse);

  for (let i = 0; i < sectionResponse.length; i += 1) {
    const sectionIdStr = String(sectionResponse[i].QuestionPaperSectionID);
    const sectionobj = {
      sectionid: sectionResponse[i].QuestionPaperSectionID,
      sectionname: sectionResponse[i].SectionTitle,
      duration: sectionResponse[i].TotalTime,
      totalmarks: sectionResponse[i].TotalMarks,
      totalquestions: sectionResponse[i].TotalQuestions,
      sectionindex: i,
      total: sectionResponse[i].ItemResponse.length,
      isContinuousQuestionNo: sectionResponse[i].IsContinuousQuestionNo || null,
      sectionLimit: sectionResponse[i].SectionLimit || null,
      parentId: sectionResponse[i].ParentId || 0,
      instruction: sectionResponse[i].Instruction || null,
      instructions: instructionData.sectionInstructionsMap[sectionIdStr] || [],
      beforeBreakTime: sectionResponse[i].BeforeBreakTime || 0,
      AfterBreakTime: sectionResponse[i].afterBreakTime || 0,
      isSectionAdaptive: sectionResponse[i].isSectionAdaptive || false,
      minimumMarks: sectionResponse[i].minimumMarks || 0,
      maximumMarks: sectionResponse[i].maximumMarks || 0,
    };

    sections.push(sectionobj);
    sectionResponse[i].ItemResponse = randomizeQuestions(
      sectionResponse[i].ItemResponse,
      random,
      shuffleQuestionsBy,
    );

    // shuffle options here

    // if (isShuffleOptions) {
    //   sectionResponse[i].ItemResponse = shuffleOptions(sectionResponse[i].ItemResponse, random);
    // }

    let ItemResponse = sectionResponse[i].ItemResponse.map(
      (qItem, itemIndex) => {
        let item = {
          ...qItem,
          qindex: qindex,
          qsectionindex: i,
          qsectionname: qItem.SectionTitle,
          qindexbysection: itemIndex,
          calculatorMode: sectionResponse[i].CalculatorMode || null,
          parentId: sectionResponse[i].ParentId || null,
        };

        qindex += 1;

        return item;
      },
    );

    questions.push(...ItemResponse);
  }

  return {
    sections,
    questions,
    ...instructionData,
  };
};

const formatQuestions = ({
  testJson,
  studentId,
  testId,
  companyCode,
  output,
}) => {
  const totalLanguages =
    testJson.Languages !== '' && testJson.Languages !== null
      ? testJson.Languages.split(',')
      : [];

  const isSectionalTimer = !!testJson.IsSectionalTimer;

  let totalTime = output.sections.reduce(
    (sum, p) => sum + Number(p.duration),
    0,
  );

  if (isSectionalTimer) {
    totalTime = Number(output.sections[0].duration);
  }

  const totalTimeInSecs = totalTime * 60;

  const formattedObj = {
    questions: output.questions,
    loading: false,
    error: null,
    studentId: studentId || null,
    testId: testId || null,
    testname: testJson.Name,
    companyCode: companyCode || null,
    totalDuration: totalTimeInSecs,
    prevTime: totalTimeInSecs,
    instruction: testJson.Instruction,
    showInstruction: true,
    totalQuestions: output.questions.length,
    totalSections: output.sections.length,
    response: [],
    tracks: [],
    sections: output.sections,
    languages: totalLanguages,
    languageIndex: 0,
    trackNumber: 1,
    question: { ...output.questions[0] },
    sectionalTimer: isSectionalTimer,
    instructions: output.instructions || [],
    globalInstructions: output.globalInstructions || [],
    sectionInstructionsMap: output.sectionInstructionsMap || {},
  };
  return formattedObj;
};

export const checkTestCompleted = ({ testId, courseId, token }) => async (dispatch, getState) => {
  const { user } = getState().auth;
  const initTest = await getInitTest(testId, courseId);
  if (initTest.completed) {
    await hardEndTest({
      testId: testId,
      token: token,
      userId: user.id
    });
    toast.error('Test was already completed');
  }

  return initTest;
};

export const launchTest = token => async dispatch => {
  try {
    dispatch({
      type: types.LAUNCH_TEST,
      payload: { launching: true, testJson: {} },
    });

    const isOnline = checkOnline();

    const offlineResponse = await getTestLaunchData({ token });

    if (!offlineResponse.success) {
      throw new Error(offlineResponse.error || 'Failed to launch test');
    }

    let resData = offlineResponse.data;

    const {
      testJson,
      studentTestData,
      tracks,
      testData,
      userData,
      companyData,
      completed,
    } = resData;

    if (isOnline) {
      const checkedTest = await dispatch(checkTestCompleted({
        testId: studentTestData.testId,
        token: token,
        userId: studentTestData.userId,
        courseId: studentTestData.courseId
      }));

      if (checkedTest.completed) {
        dispatch({
          type: types.TEST_COMPLETED,
          payload: { launching: false, testCompleted: true },
        });
        return;
      }
    }

    if (completed) {
      dispatch(
        syncOfflineTracks({
          testId: studentTestData.testId,
          courseId: studentTestData.courseId,
          end: true,
          redirectToken: token
        }),
      );
      dispatch({
        type: types.TEST_COMPLETED,
        payload: { launching: false, testCompleted: completed },
      });
      return;
    }
    const sectionIndex = 0;

    const output = getSectionsAndQuestions(
      testJson.SectionResponse,
      testData.randomizeNo,
      testData.shuffleQuestionsBy,
      testData.isShuffleOptions,
    );

    const section = output.sections[sectionIndex];

    const totalQuestions = output.questions?.length || 0;

    const groupQuestions = groupByPassage(output.questions, 'ItemPassageID');

    const questionGrps = Object.keys(groupQuestions)
      .map((psgId, pIndex) => {
        const qns = groupQuestions[psgId];
        const firstQn = qns[0] || {};
        return {
          grpIndex: pIndex,
          passageId: firstQn.ItemPassageID || null,
          passage: firstQn.Passage || firstQn.Items || null,
          qindexbysection: firstQn.qindexbysection,
          passageItemIndex: null,
          passageTitle: firstQn.PassageTitle || null,
          questions: qns,
          audioUrl: firstQn.PassageAudioUrl || firstQn.AudioURL || null,
          audioPlayCount: firstQn.audioPlayCount || 1,
          isPassageQn: !!firstQn.ItemPassageID,
        };
      })
      .sort((a, b) => a.grpIndex - b.grpIndex);

    const totalQuestionGrps = questionGrps.length;

    dispatch({
      type: types.LAUNCH_TEST,
      payload: {
        launching: false,
        activeSection: section,
        sectionIndex,
        totalQuestions,
        sections: output.sections,
        questions: output.questions,
        totalQuestionGrps,
        questionGrps,
        activeQuestion: questionGrps[0],
        activeIndex: 0,
        currentPsgPos: 0,
        isFirstPsg: true,
        isLastPsg: totalQuestionGrps === 1,
        ...resData,
      },
    });

    const studentId = studentTestData.userId;
    const testId = studentTestData.testId;
    const companyCode = studentTestData.companyCode;

    let framedData = formatQuestions({
      testJson,
      studentId,
      testId,
      companyCode,
      output,
    });

    framedData.showInstruction = !(
      framedData.instruction == null || framedData.instruction == ''
    );

    framedData.userData = userData;
    framedData.companyData = companyData;

    // for administrator

    framedData.isAdministrator = studentTestData.isAdministrator || 0;

    framedData.testTemplate = testData.testTemplate || null;

  framedData.instructions = output.instructions || [];
  framedData.globalInstructions = output.globalInstructions || [];
  framedData.sectionInstructionsMap = output.sectionInstructionsMap || {};

    // Resume logic: find first passage group containing an unanswered question
    let lastQuestionId = output.questions[0].ItemID;
    let shouldInitInstructions = false;
    if (tracks.length > 0) {
      framedData.studentResponse = extractResponses(tracks);
      framedData.showInstruction = false;
      framedData.mainTimerStarted = true;
      framedData.token = token;
      framedData.showReport = testData.showReport;
      framedData.totalDuration = tracks[tracks.length - 1].timeRemaining;
      framedData.prevTime = tracks[tracks.length - 1].timeRemaining;
      framedData.trackNumber = tracks[tracks.length - 1].trackNumber + 1;
      dispatch(startTimer(framedData.totalDuration));

      // Build set of answered itemIds
      const answeredItemIds = new Set(tracks.map(t => Number(t.itemId)));
      // Find first passage group containing an unanswered question
      let found = false;
      for (let grp of questionGrps) {
        for (let q of grp.questions) {
          if (!answeredItemIds.has(q.ItemID)) {
            lastQuestionId = q.ItemID;
            found = true;
            break;
          }
        }
        if (found) break;
      }
      if (!found) {
        // If all answered, resume at last question
        lastQuestionId = output.questions[output.questions.length - 1].ItemID;
      }
    } else {
      framedData.token = token;
      framedData.showInstruction = false;
      framedData.mainTimerStarted = false;
      const hasNewInstructions =
        (output.globalInstructions || []).length > 0 ||
        Object.keys(output.sectionInstructionsMap || {}).length > 0;
      if (!hasNewInstructions && !framedData.showInstruction) {
        dispatch(startTimer(framedData.totalDuration));
        framedData.mainTimerStarted = true;
      } else if (hasNewInstructions) {
        shouldInitInstructions = true;
      } else if (!framedData.showInstruction) {
        dispatch(startTimer(framedData.totalDuration));
        framedData.mainTimerStarted = true;
      }
    }

    // Determine active index/group based on lastQuestionId (for resume)
    const activeIndex = questionGrps.findIndex(grp =>
      grp.questions.some(q => String(q.ItemID) === String(lastQuestionId)),
    );
    const currentPsgPos = activeIndex >= 0 ? activeIndex : 0;
    const activeQuestion = questionGrps[currentPsgPos] || questionGrps[0];
    const isFirstPsg = currentPsgPos === 0;
    const isLastPsg = currentPsgPos === totalQuestionGrps - 1;

    dispatch({
      type: types.LAUNCH_TEST,
      payload: {
        launching: false,
        ...resData,
        ...framedData,
        activeQuestion,
        activeIndex,
        currentPsgPos,
        isFirstPsg,
        isLastPsg,
      },
    });

    if (shouldInitInstructions) {
      dispatch(initInstructionFlow());
    } else {
      dispatch(gotoQuestion(lastQuestionId));
    }
  } catch (error) {
    dispatch({
      type: types.LAUNCH_TEST,
      payload: {
        launching: false,
        testJson: {},
        error: error.message || 'Something went wrong. Contact administrator',
      },
    });
  }
};

export const startTest = () => (dispatch, getState) => {
  const state = getState();

  dispatch({
    type: types.START_TEST,
    payload: {
      showInstruction: false,
    },
  });

  dispatch(startTimer(state.engine.totalDuration));
};

export const endSection = () => (dispatch, getState) => {
  // Functionality Not completed //
  const state = getState();
  const { activeQuestion, sections } = state.engine;
  const nextSectionIndex = activeQuestion.qsectionindex + 1;

  if (nextSectionIndex <= sections.length) {
    const getSection = sections.filter(
      p => p.sectionindex === nextSectionIndex,
    );
    const sectionTime = Number(getSection[0].duration) * 60;
    const updatedobj = {};
    updatedobj.totalDuration = sectionTime;
    updatedobj.prevTime = sectionTime;

    // dispatch({
    //   type: types.SET_TEST_DATA,
    //   payload: {
    //     ...updatedobj,
    //   },
    // });
    // dispatch(updateTimer(updatedobj));
    // dispatch(startTimer(sectionTime));
    // dispatch(goToSection(getSection[0].sectionid));
  }
};

const syncsuccess = (oldTracks, newTracks) => {
  const updatedTracks = oldTracks.map(item => {
    let existItem = newTracks.find(p => p.id === item.id);
    if (existItem) {
      return {
        ...item,
        saved: 1,
      };
    }
    return item;
  });

  const updateObj = {
    tracks: updatedTracks,
    synError: null,
  };

  return updateObj;
};

const syncsuccessOffline = (oldTracks, newTracks) => {
  const updatedTracks = oldTracks.map(item => {
    let existItem = newTracks.find(p => p.id === item.id);
    if (existItem) {
      return {
        ...item,
        offlineSaved: 1,
      };
    }
    return item;
  });

  const updateObj = {
    tracks: updatedTracks,
    synError: null,
  };

  return updateObj;
};

export const handleEndTest = () => dispatch => {
  const isOnline = checkOnline();

  if (!isOnline) {
    toast.info(
      'It seems you\'re offline. Sync your test whenever you\'re online',
    );
  }

  dispatch(syncTrack(true));
};

const endTest = (syncToken, reload = true) => async (dispatch, getState) => {
  try {
    const { token, testId } = getState().engine;
    let testToken = token;
    if (!testToken) {
      testToken = syncToken;
    }
    if (!testToken) {
      // Fallback: try to get from cookies
      testToken = Cookies.get('testToken') || null;
    }
    const payload = {
      token: testToken,
    };

    dispatch({
      type: types.END_TEST_START,
      payload: { ending: true },
    });

    // Only online API call, no DB/upload/download
    await Service.POST({
      name: 'end-test',
      payload: payload,
      testengine: true,
    }).then(res => {
      if (res.success) {
        return res.data;
      }
      throw new Error(res.message);
    });

    dispatch(clearTimer());

    dispatch({ type: types.END_TEST_SUCCESS, payload: { ending: false, submitting: false } });

    if (reload) {
      const rurl = localStorage.getItem('rurl');
      if (rurl) {
        openInNewTab(rurl);
        localStorage.removeItem('rurl');
      } else if (testId) {
        openInNewTab(`https://portal.kananprep.com/report/${testId}`);
      } else {
        openInNewTab('/');
      }
    } else {
      dispatch(getAllTests());
    }
  } catch (error) {
    dispatch({
      type: types.END_TEST_FAILED,
      payload: {
        ending: false,
        submitting: false,
        error:
          'SUBMIT ERROR: Submit not successful. Please contact administrator!',
      },
    });
  }
};

const limitArray = (arr) => {
  const limitedArr = arr.filter((item, index) => index < 21);
  return limitedArr;
};

export const syncTrack = (end = 0, isEvaluationTest) => async (dispatch, getState) => {
  const isOnline = checkOnline();
  const { engine } = getState();

  const tracks = limitArray(engine.tracks.filter(p => p.saved === 0));

  let i,
    j,
    chunk = 20;

  const reload = isEvaluationTest ? false : true;

  for (i = 0, j = tracks.length; i < j; i += chunk) {
    const data = {
      track: tracks.slice(i, i + chunk).map(t => ({
        id: t._id ? String(t._id) : '',
        testId: t.testId ? String(t.testId) : '',
        studentId: t.studentId ? String(t.studentId) : '',
        itemId: t.itemId != null ? String(t.itemId) : null,
        sectionId: t.sectionId != null ? String(t.sectionId) : null,
        sectionIndex: t.sectionIndex != null && !isNaN(Number(t.sectionIndex)) ? Number(t.sectionIndex) : 0,
        questionIndex: t.questionIndex != null && !isNaN(Number(t.questionIndex)) ? Number(t.questionIndex) : 0,
        subjectId: t.subjectId != null ? String(t.subjectId) : null,
        areaId: t.areaId != null ? String(t.areaId) : null,
        topicId: t.topicId != null ? String(t.topicId) : null,
        timeRemaining: t.timeRemaining != null && !isNaN(Number(t.timeRemaining)) ? Number(t.timeRemaining) : 0,
        isSectionCompleted: t.isSectionCompleted == null ? null : Boolean(t.isSectionCompleted),
        selectedOptionId: t.selectedOptionId != null ? String(t.selectedOptionId) : null,
        attempt: t.attempt == null ? null : Boolean(t.attempt),
        itemType: t.itemType != null ? String(t.itemType) : null,
        marked: t.marked == null ? null : Boolean(t.marked),
        timeTaken: t.timeTaken != null && !isNaN(Number(t.timeTaken)) ? Number(t.timeTaken) : 0,
        timeStamp: t.timeStamp ? String(t.timeStamp) : '',
        companyCode: t.companyCode ? String(t.companyCode) : '',
        points: t.points != null && !isNaN(Number(t.points)) ? Number(t.points) : 0,
        negativePoints: t.negativePoints != null && !isNaN(Number(t.negativePoints)) ? Number(t.negativePoints) : 0,
        isCorrect: t.isCorrect == null ? null : Boolean(t.isCorrect),
        enteredText: t.enteredText != null ? String(t.enteredText) : null,
        trackNumber: t.trackNumber != null && !isNaN(Number(t.trackNumber)) ? Number(t.trackNumber) : 0,
        difficultyId: t.difficultyId != null ? String(t.difficultyId) : null,
        deleteOn: t.deleteOn != null && !isNaN(Number(t.deleteOn)) ? Number(t.deleteOn) : 0,
        ipAddress: t.ipAddress ? String(t.ipAddress) : '192.168.1.1',
      })),
    };

    if (tracks.length > 0) {
      await putTracks(tracks, engine.token);

      if (isOnline) {
        await Service.POST({
          name: 'sync-track',
          payload: data,
          testengine: true,
        }).then(async res => {
          if (res?.data?.error) {
            dispatch({
              type: types.SYNC_FAILED,
              error: {
                message:
                  'SYNC ERROR: Responses are not saved. Please contact administrator!',
              },
            });
          } else {
            const syncRes = syncsuccess(engine.tracks, tracks);
            await putTracks(syncRes.tracks, engine.token);
            dispatch({
              type: types.SYNC_SUCCESS,
              payload: { ...syncRes },
            });
            if (end) {
              dispatch(endTest(null, reload));
            }
          }
        });
      } else {
        const syncRes = syncsuccessOffline(engine.tracks, tracks);
        await putTracks(syncRes.tracks, engine.token);
        dispatch({
          type: types.SYNC_SUCCESS,
          payload: { ...syncRes },
        });
        if (end) {
          dispatch(endTest(null, reload));
        }
      }
    } else {
      dispatch({
        type: types.SYNC_NOITEMS,
      });
      if (end) {
        dispatch(endTest(null, reload));
      }
    }
  }

  if (end && tracks.length === 0) {
    dispatch(endTest(null, reload));
  }
};

export const hardEndTest = async ({ token, testId, userId }) => {
  await dexieEndTest({ token });
  await dexieSyncTest({ testId, userId });
  await deleteTestLaunchData({ token });
};

export const handleSyncTest = (record) => async (dispatch, getState) => {
  const isOnline = checkOnline();
  const { user } = getState().auth;

  if (!isOnline) {
    toast.info(
      'It seems you\'re offline. Upload your test when you\'re online',
    );
    return;
  }

  const offlineResponse = await getTestToken({
    testId: record.testId,
    courseId: record.courseId,
    userId: user.id,
  });

  const checkedTest = await dispatch(checkTestCompleted({
    testId: record.testId,
    userId: user.id,
    courseId: record.courseId,
    token: offlineResponse.token
  }));

  if (checkedTest.completed) {
    dispatch({
      type: types.TEST_COMPLETED,
      payload: { launching: false, testCompleted: true },
    });
    dispatch(getAllTests());
    return;
  }

  dispatch(syncOfflineTracks({
    testId: record.testId,
    courseId: record.courseId,
    end: true,
    redirectToken: checkedTest.redirectToken
  }));
};

export const handleUploadAll = () => async (dispatch, getState) => {
  const isOnline = checkOnline();
  const { tests } = getState().test;
  if (!isOnline) {
    toast.info(
      'It seems you\'re offline. Upload your test when you\'re online',
    );
    return;
  }

  const syncTest = tests.some((test) =>
    test.subItems.some((subItem) => subItem.studentTestDetails.isSynced === 0)
  );

  if (!syncTest) {
    toast.info(
      'All your completed tests are already uploaded.',
    );
    return;
  }

  for (const test of tests) {
    if (test.subItems.length) {
      for (const item of test.subItems) {
        if (item.studentTestDetails.isSynced === 0) {
          try {
            await dispatch(handleSyncTest({
              testId: item.id,
              courseId: item.courseId,
              end: true,
            }));
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`Error syncing test: ${item.id}`, e);
          }
        }
      }
    }
  }
};

export const syncOfflineTracks = ({
  testId,
  courseId,
  end = false,
  redirectToken
}) => async (dispatch, getState) => {
  try {
    const isOnline = checkOnline();
    const { user } = getState().auth;
    if (!isOnline) {
      toast.info(
        'It seems you\'re offline. Upload your test when you\'re online',
      );
      return;
    }

    dispatch({
      type: types.SYNC_RECORDS,
      payload: { syncId: testId, syncing: true },
    });

    const engine = await getAllTracks({ testId, courseId, userId: user.id });

    if (!engine.success) {
      toast.error('Sync Failed');
      throw new Error('Sync Failed');
    }

    await getEngineToken(redirectToken);

    let unsyncedTracks = engine.tracks.filter(p => p.saved === 0);
    if (unsyncedTracks.length === 0) {
      if (end) {
        await dispatch(endTest(engine.testData.token, false));
        await dexieSyncTest({ testId, courseId, userId: user.id });
        dispatch(getAllTests());
        toast.info('Test already synced');
      }
      dispatch({
        type: types.SYNC_RECORDS,
        payload: { syncId: null, syncing: false },
      });
      return;
    }

    let i,
      j,
      records,
      chunk = 10;

    for (i = 0, j = unsyncedTracks.length; i < j; i += chunk) {
      records = unsyncedTracks.slice(i, i + chunk);

      await Service.POST({
        name: 'sync-track',
        payload: {
          track: records.map(t => ({ ...t, id: t._id })),
        },
        testengine: true,
      }).then(async res => {
        if (res?.data?.error) {
          dispatch({
            type: types.SYNC_FAILED,
            error: {
              message:
                'SYNC ERROR: Responses are not saved. Please contact administrator!',
            },
          });
        } else {
          const syncRes = syncsuccess(engine.tracks, records);
          await putTracks(syncRes.tracks, engine.testData.token);
          dispatch({
            type: types.SYNC_SUCCESS,
            payload: { ...syncRes },
          });
        }
      });
    }

    if (end) {
      await dispatch(endTest(engine.testData.token, false));
      await dexieSyncTest({
        testId: engine.testData.id,
        userId: engine.testData.userId,
      });
    }

    dispatch({
      type: types.SYNC_RECORDS,
      payload: { syncId: '', syncing: false },
    });
  } catch (error) {
    dispatch({
      type: types.SYNC_NOITEMS,
    });
  }
};

export const downloadTestEngineLaunch = (testToken) => async dispatch => {
  try {
    // Step 1: Get token via test → validate-token flow
    // const initTest = await getInitTest(testId, courseId);

    // // If test is already completed, stop here and show completed screen
    // if (initTest.completed) {
    //   dispatch({
    //     type: types.TEST_COMPLETED,
    //     payload: { launching: false, testCompleted: true },
    //   });
    //   return;
    // }

    // const validateTest = await getEngineToken(initTest.redirectToken);
    // const urlParams = new URLSearchParams(validateTest.redirectLink?.split('?')[1]);
    // const testToken = urlParams.get('token');
    // console.log(testToken, 'testToken');
    // console.log(validateTest.toen, 'test-token');
    // Cookies.set('testToken', testToken);

    // Step 2: Fetch questions + tracks from test-launch
    const launchRes = await Service.POST({
      name: 'test-launch',
      payload: { token: testToken },
      testengine: true,
    });

    if (launchRes.success) {
      // toast.success('Test ready.', { toastId: 'Test Ready' });
      try {
        const testJson = launchRes.data.testJson || {};
        const tracks = launchRes.data.tracks || [];
        const studentTestData = launchRes.data.studentTestData || {};
        const testData = launchRes.data.testData || {};
        const userData = launchRes.data.userData || {};
        const companyData = launchRes.data.companyData || {};

        const studentId = studentTestData.userId || null;
        const resolvedTestId = studentTestData.testId || null;
        const companyCode = studentTestData.companyCode || null;

        // Index questions via getSectionsAndQuestions (sets qindex, qsectionindex, qindexbysection)
        const output = getSectionsAndQuestions(
          testJson.SectionResponse || [],
          0,
          null,
        );
        const questions = output.questions;

        // Group questions by passage (one entry per passage, with all questions in that passage)
        const grouped = groupByPassage(questions, 'ItemPassageID');
        const questionGrps = Object.keys(grouped)
          .map((psgId, pIndex) => {
            const qns = grouped[psgId];
            const firstQn = qns[0] || {};
            return {
              grpIndex: pIndex,
              passageId: firstQn.ItemPassageID || null,
              passage: firstQn.Passage || firstQn.Items || null,
              qindexbysection: firstQn.qindexbysection,
              passageItemIndex: null,
              passageTitle: firstQn.PassageTitle || null,
              questions: qns,
              audioUrl: firstQn.PassageAudioUrl || firstQn.AudioURL || null,
              audioPlayCount: firstQn.audioPlayCount || 1,
              isPassageQn: !!firstQn.ItemPassageID,
            };
          })
          .sort((a, b) => a.grpIndex - b.grpIndex);

        // Mark answered state from tracks (updates question objects shared with questionGrps)
        const answeredMap = {};
        tracks.forEach(track => {
          answeredMap[`${track.sectionId}_${track.itemId}`] = track;
        });
        questions.forEach(qn => {
          const key = `${qn.QuestionPaperSectionID}_${qn.ItemID}`;
          if (answeredMap[key]) {
            qn.answered = true;
            qn.selectedOptionId = answeredMap[key].selectedOptionId;
            qn.enteredText = answeredMap[key].enteredText;
            qn.isCorrect = answeredMap[key].isCorrect;
            qn.marked = answeredMap[key].marked;
            qn.attempt = answeredMap[key].attempt;
          } else {
            qn.answered = false;
          }
        });

        const totalQuestionGrps = questionGrps.length;
        const totalQuestions = questions.length;

        // Determine where to resume: next unanswered question after last tracked answer
        let resumeItemId = tracks.length > 0 ? tracks[tracks.length - 1].itemId : null;
        let resumeIndex = 0;
        if (resumeItemId != null) {
          const flatQns = questions;
          let idx = flatQns.findIndex(q => String(q.ItemID) === String(resumeItemId));
          const nextIdx = flatQns.slice(idx + 1).findIndex(q => !q.answered);
          if (nextIdx !== -1) {
            idx = idx + 1 + nextIdx;
            const targetItemId = String(flatQns[idx].ItemID);
            resumeIndex = questionGrps.findIndex(grp =>
              grp.questions.some(q => String(q.ItemID) === targetItemId),
            );
          } else {
            const firstUnanswered = flatQns.find(q => !q.answered);
            if (firstUnanswered) {
              const targetId = String(firstUnanswered.ItemID);
              resumeIndex = questionGrps.findIndex(grp =>
                grp.questions.some(q => String(q.ItemID) === targetId),
              );
            }
          }
        } else {
          const firstUnanswered = questions.find(q => !q.answered);
          if (firstUnanswered) {
            const targetId = String(firstUnanswered.ItemID);
            resumeIndex = questionGrps.findIndex(grp =>
              grp.questions.some(q => String(q.ItemID) === targetId),
            );
          }
        }
        const currentPsgPos = resumeIndex >= 0 ? resumeIndex : 0;
        const activeQuestion = questionGrps[currentPsgPos] || questionGrps[0];
        const activeIndex = currentPsgPos;
        const isFirstPsg = currentPsgPos === 0;
        const isLastPsg = currentPsgPos === totalQuestionGrps - 1;

        // Build studentResponse and track sequence
        const studentResponse = tracks.length > 0 ? extractResponses(tracks) : [];
        const trackNumber = tracks.length > 0 ? tracks[tracks.length - 1].trackNumber + 1 : 1;

        // Calculate total duration from sections (base) and optionally use last track's timeRemaining
        const isSectionalTimer = !!testJson.IsSectionalTimer;
        let totalTime = output.sections.reduce((sum, s) => sum + Number(s.duration), 0);
        if (isSectionalTimer) totalTime = Number(output.sections[0]?.duration || 0);
        const sectionTotalDuration = totalTime * 60;

        // Only use track's timeRemaining if it's a valid positive number
        const lastTimeRemaining = tracks.length > 0 ? tracks[tracks.length - 1].timeRemaining : null;
        const totalDuration = (lastTimeRemaining != null && Number(lastTimeRemaining) > 0)
          ? Number(lastTimeRemaining)
          : sectionTotalDuration;

        dispatch({ type: types.SET_ENGINE_QUESTIONS, payload: questionGrps });
        dispatch({ type: types.SET_ENGINE_TRACKS, payload: tracks });
        dispatch({
          type: types.LAUNCH_TEST,
          payload: {
            launching: false,
            activeSection: output.sections[0],
            sectionIndex: 0,
            totalQuestions,
            sections: output.sections,
            questions,
            totalQuestionGrps,
            questionGrps,
            activeQuestion,
            activeIndex,
            currentPsgPos,
            isFirstPsg,
            isLastPsg,
            // session identity — required for frameTrack/savetrack
            testId: resolvedTestId,
            studentId,
            companyCode,
            token: testToken,
            trackNumber,
            totalDuration,
            prevTime: totalDuration,
            // test meta
            testJson,
            testData,
            userData,
            companyData,
            studentTestData,
            isAdministrator: studentTestData.isAdministrator || 0,
            testTemplate: testData.testTemplate || null,
            showReport: testData.showReport || null,
            showInstruction: false,
            instructions: output.instructions || [],
            globalInstructions: output.globalInstructions || [],
            sectionInstructionsMap: output.sectionInstructionsMap || {},
            mainTimerStarted: tracks.length > 0,
            // student state
            studentResponse,
          },
        });

        if (tracks.length > 0) {
          if (totalDuration > 0) {
            dispatch(startTimer(totalDuration));
          }
        } else {
          const hasNewInstructions =
            (output.globalInstructions || []).length > 0 ||
            Object.keys(output.sectionInstructionsMap || {}).length > 0;
          if (hasNewInstructions) {
            dispatch(initInstructionFlow());
          } else if (totalDuration > 0) {
            dispatch(startTimer(totalDuration));
            dispatch({
              type: types.SET_TEST_DATA,
              payload: { mainTimerStarted: true },
            });
          }
        }
      } catch (err) {
        toast.error('Failed to map or dispatch test data.');
      }
    }
  } catch (error) {
    toast.error('Failed to load test data.');
  }
};
